import { useQuery, type UseQueryResult, type UseQueryOptions } from '@tanstack/react-query'
import { fetchForUrl } from '@/shared/utils/api'

// This is a very thin wrapper around react-query's useQuery hook that fetches data from a given URL.
// It simplifies our most common data fetching pattern of fetching a resource once the URL is defined.
// Supports all useQuery options.
//
// Usage:
//
// const { data, isLoading, error, refetch } = useQueryUrl<ICall>(callUrl)
//
// // With additional options:
// const { data } = useQueryUrl<ICall>(callUrl, {
//   placeholderData: keepPreviousData,
//   select: (data) => data.entries,
//   staleTime: 30000, // override default staleTime
//   gcTime: 60000, // keep in cache for 1 minute
// })
//
// return <div>{data?.title}</div>
//
type UseQueryUrlOptions<TData, TResult = TData> = Omit<
  UseQueryOptions<TData, Error, TResult, [string]>,
  'queryKey' | 'queryFn'
>

export function useQueryUrl<TData, TResult = TData>(
  url: Nullable<string>,
  options?: UseQueryUrlOptions<TData, TResult>,
): UseQueryResult<TResult> {
  return useQuery({
    queryKey: [url!], // Asserting that the URL will be defined at the time we make the request.
    queryFn: fetchForUrl<TData>,
    enabled: !!url, // Don't consider this an active query until the URL is defined.
    ...options,
  })
}
